import type { VoidFunctionComponent } from 'react';
import { Modal } from 'react-bootstrap';
import Button, { ButtonGroup } from '../../generic/Button';
import { LabeledTextInput } from '../../generic/Forms';
import { useInputValue } from '../../../hooks';
import { onEnter } from '../../../utils';
import type { AsyncStatus } from '../../../types';
import { ErrorAlert } from '../../generic';
import { FILTER_NAME_SIZE_LIMIT } from '../../../../shared/savedFilters';

type Props = {
  currentName: string;
  isDisplayed: boolean;
  isSaving: boolean;
  errors?: AsyncStatus['errors'];
  onRename: (name: string) => void;
  onHide: () => void;
};

/**
 * RenameModal allows the user to rename the currentFilter state as a new filter.
 */
const RenameModal: VoidFunctionComponent<Props> = ({
  currentName,
  isDisplayed,
  isSaving,
  errors,
  onHide,
  onRename,
}) => {
  const [name, setName] = useInputValue(currentName || '');
  const isSubmitDisabled = isSaving || currentName === name || name.trim().length < 1;
  //  Submit on keydown
  const onEnterKeyDown = onEnter(() => !isSubmitDisabled && onRename(name));
  return (
    <Modal
      animation
      show={isDisplayed}
      onHide={onHide}
      onEscapeKeyDown={onHide}
    >
      <Modal.Header>
        <Modal.Title>
          Rename filter
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <LabeledTextInput
          label="Name"
          placeholder="Name filter"
          value={name}
          onChange={setName}
          onKeyDown={onEnterKeyDown}
          maxLength={FILTER_NAME_SIZE_LIMIT}
          showCharCount
          autoFocus
          testId="saved-filters-modal-input--rename"
        />
        {errors && errors.length > 0 && (
          <div className="margin-top-md">
            <ErrorAlert status={{ errors }} />
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <ButtonGroup>
          <Button
            type="secondary"
            size="medium"
            onClick={onHide}
            isDisabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            size="medium"
            onClick={() => onRename(name)}
            isDisabled={isSubmitDisabled}
            testId="saved-filters-modal-submit--rename"
          >
            Rename filter
          </Button>
        </ButtonGroup>
      </Modal.Footer>
    </Modal>
  );
};

export default RenameModal;
